import React, { FC, useEffect, useState } from 'react'

import cn from 'classnames'
import { OTPInput, SlotProps } from 'input-otp'

import styles from './OtpInput.module.scss'

interface OtpInputProps {
  hasError?: boolean
  disabled?: boolean
  autoFocus?: boolean
  value: string
  onChange?: (value: string) => void
  isLoading?: boolean
  onComplete: (value: string) => void
  size?: OtpInputSizes
  variant?: OtpInputVariants
}

export enum OtpInputSizes {
  S = 'S',
  M = 'M',
  L = 'L'
}

export enum OtpInputVariants {
  Light = 'Light',
  Dark = 'Dark'
}

const Slot: FC<
  SlotProps & {
    isLoading: boolean
    size: OtpInputSizes
    variant: OtpInputVariants
    hasError?: boolean
  }
> = ({ char, hasFakeCaret, isActive, isLoading, size, variant, hasError }) => {
  return (
    <div
      className={cn(styles.slot, {
        [styles.active]: isActive,
        [styles.loading]: isLoading,
        [styles.small]: size === OtpInputSizes.S,
        [styles.medium]: size === OtpInputSizes.M,
        [styles.large]: size === OtpInputSizes.L,
        [styles.light]: variant === OtpInputVariants.Light,
        [styles.dark]: variant === OtpInputVariants.Dark,
        [styles.error]: hasError
      })}
    >
      <span>{char}</span>
      {hasFakeCaret && <div className={styles.caret} />}
    </div>
  )
}

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  isLoading,
  onComplete,
  disabled,
  autoFocus,
  size = OtpInputSizes.M,
  hasError,
  variant = OtpInputVariants.Light
}) => {
  const [shouldShake, setShouldShake] = useState(false)

  useEffect(() => {
    if (hasError) {
      setShouldShake(true)
      const timeoutId = setTimeout(() => {
        setShouldShake(false)
      }, 400)
      return () => clearTimeout(timeoutId)
    } else {
      setShouldShake(false)
    }
  }, [hasError])

  return (
    <div className={styles.OtpInput}>
      <OTPInput
        containerClassName={cn(styles.slots, {
          [styles.shake]: shouldShake
        })}
        autoFocus={autoFocus}
        value={value}
        maxLength={6}
        render={({ slots }) => {
          return slots.map((slot, i) => (
            <Slot
              hasError={hasError}
              key={i}
              {...slot}
              size={size}
              isLoading={Boolean(isLoading)}
              variant={variant}
            />
          ))
        }}
        onComplete={isLoading ? undefined : onComplete}
        onChange={onChange}
        disabled={disabled || isLoading}
      />
    </div>
  )
}

export { OtpInput }
